import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FormattedMessage } from "react-intl";

/* eslint-disable no-undef */

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;

const loadTurnstileScript = () => {
  if (typeof window !== "undefined" && window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("Turnstile script failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

const TurnstileWidget = forwardRef(({ onToken, locale }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [scriptFailed, setScriptFailed] = useState(false);
  const siteKey = process.env.TURNSTILE_SITE_KEY;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    if (!siteKey) return undefined;

    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null)
          return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          language: locale === "fr-fr" ? "fr" : locale || "auto",
          callback: (token) => onToken(token),
          "expired-callback": () => onToken(null),
          "error-callback": () => onToken(null),
        });
      })
      .catch(() => {
        if (!cancelled) setScriptFailed(true);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, locale]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0.5rem",
        mt: "0.75rem",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        <FormattedMessage id="form_validation.captcha_required" />
      </Typography>
      {scriptFailed || !siteKey ? (
        <Typography variant="caption" color="error">
          <FormattedMessage id="form_validation.captcha_unavailable" />
        </Typography>
      ) : (
        <div ref={containerRef} />
      )}
    </Box>
  );
});

TurnstileWidget.displayName = "TurnstileWidget";

TurnstileWidget.propTypes = {
  onToken: PropTypes.func.isRequired,
  locale: PropTypes.string,
};

export default TurnstileWidget;
